import { React, PropTypes } from 'imports';
import { FormattedResource } from '..';
import { isAncestorOrSelf } from 'utils';
import './MultiEmailInput.less';

const IS_IE = (() => !/*@cc_on!@*/true)() || !!document.documentMode;

export const SEPARATORS_RE = /[\s,;]+/g;
export const SEPARATORS = ['\u0009', '\u000B', '\u000D', ' ', ',', ';', '\u00A0', '\u1680', '\u2000', '\u2002', '\u2004', '\u2006', '\u2008', '\u200A', '\u2028', '\u202F', '\u205F', '\u3000', '\uFEFF']; //Array.from(Array(65535)).map((_, index) => String.fromCharCode(index)).filter(char => SEPARATORS_RE.test(char)).map(char => char.charCodeAt(0)).map(code=> code > 31 && code<127?String.fromCharCode(code):'\\u' + code.toString(16).padLeft(4,'0').toUpperCase())

export default class MultiEmailInput extends React.Component {
    static get SEPARATORS_RE() {
        return SEPARATORS_RE;
    }

    static get SEPARATORS() {
        return SEPARATORS;
    }

    static get propTypes() {
        return {
            value: PropTypes.arrayOf(PropTypes.string),
            placeholder: FormattedResource.resourcePropType,
            clearable: PropTypes.bool,
            disabled: PropTypes.bool,
            className: PropTypes.string,
            onChange: PropTypes.func
        };
    }

    static get contextTypes() {
        return {
            intl: PropTypes.object
        }
    }

    static get defaultProps() {
        return {
            value: [],
            placeholder: '',
            clearable: true,
            disabled: false,
            className: ''
        }
    }

    state = { value: [], pendingText: '' };

    _cachedPendingInnerText = '';

    _getPendingInnerText(forceUpdateCache) {
        if (!this.refs['pending-text']) {
            return '';
        }
        return !forceUpdateCache ?
            this._cachedPendingInnerText :
            this._cachedPendingInnerText = this.refs['pending-text'].innerText
                .replace(/\u00A0/g, ' ') // browsers intersperse spaces with nbsp in long spaces sequences to prevent space collapsing
                .replace(!IS_IE ? /\n$/g : '', '') // https://stackoverflow.com/questions/27786048/why-is-my-contenteditable-cursor-jumping-to-the-end-in-chrome
            ;
    }

    _setPendingInnerText(value) {
        if (!this.refs['pending-text'] || this._getPendingInnerText() === value) {
            return;
        }
        this.refs['pending-text'].innerText = this._cachedPendingInnerText = value
            .replace(/\n/g, ' ')
            .replace(/  /g, ' \u00A0') +
            (!IS_IE ? '\n' : '');
    }

    componentWillMount() {
        this.componentWillReceiveProps(this.props);
    }

    componentDidMount() {
        this.componentDidUpdate();
    }

    componentWillReceiveProps(nextProps) {
        this.setState({ value: nextProps.value });
    }

    shouldComponentUpdate(nextProps, nextState, nextContext) {
        return false ||
            this.state.value.toString() !== nextState.value.toString() ||
            !!this.state.pendingText ^ !!nextState.pendingText ||
            !FormattedResource.equal(this.props.placeholder, nextProps.placeholder) ||
            this.context.intl.locale !== nextContext.intl.locale ||
            this.props.clearable !== nextProps.clearable ||
            this.props.disabled !== nextProps.disabled ||
            this.props.className !== nextProps.className;
    }

    componentDidUpdate(prevProps) {
        this._setPendingInnerText(this.state.pendingText);
    }

    _onChange(value, pendingText) {
        // currently we use email as a React key of a deletable email element to make deletion work properly, but it prevents us from having the same address twice
        // TODO does having the same email in a list twice have any purpose? should we make it somehow more understandable than just ignore duplicates otherwise? find an answer
        value = value.filter((email, index) => value.lastIndexOf(email) === index);

        if (this.props.onChange && this.state.value.toString() !== value.toString()) {
            this.props.onChange(this, value);
        }
        this.setState({ value, pendingText });
    }

    _onPendingTextChange = (force) => {
        const pendingText = this._getPendingInnerText(true),
            collapsedSeparator = SEPARATORS[0],
            collapsedPendingText = pendingText.replace(SEPARATORS_RE, collapsedSeparator);
        if (collapsedPendingText && collapsedPendingText !== collapsedSeparator && (force || collapsedPendingText[collapsedPendingText.length - 1] === collapsedSeparator)) {
            this._onChange(this.state.value.concat(collapsedPendingText.trim().split(collapsedSeparator)), '');
        }
        else {
            this.setState({ pendingText });
        }
    }

    _onInput = () => {
        if (this.props.disabled) {
            return;
        }

        this._onPendingTextChange();
    }

    _onPaste = (event) => {
        if (this.props.disabled) {
            return;
        }

        document.execCommand("insertHTML", false, event.clipboardData.getData("text/plain"));

        this._onPendingTextChange(true);

        event.stopPropagation();
        event.preventDefault();
    }

    _onClick = (event) => {
        if (this.props.disabled) {
            return;
        }

        if (IS_IE) {
            this.refs['pending-text'].focus();
        }
    }

    _onKeyDown = (event) => {
        if (this.props.disabled) {
            return;
        }

        if (event.key === 'Enter') {
            this._onPendingTextChange(true);

            event.stopPropagation();
            event.preventDefault();
        }
        else if (
            event.key === 'ArrowLeft' ||
            event.key === 'ArrowRight' ||
            event.key === 'Home' ||
            event.key === 'End' ||
            event.key === 'Delete' ||
            event.key === 'Backspace'
        ) {

            const
                pendingTextIndex = this.state.value.length,
                prevSelectedIndex = isAncestorOrSelf(this.refs['pending-text'], document.activeElement) ?
                    window.getSelection().anchorOffset === 0 || !this._getPendingInnerText() ? pendingTextIndex : -1 :
                    this.state.value.findIndex((value, index) => isAncestorOrSelf(this.refs['email-' + index], document.activeElement)),
                direction = event.key === 'ArrowLeft' ||
                    event.key === 'Backspace' && (prevSelectedIndex !== 0 && prevSelectedIndex !== pendingTextIndex - 1 && prevSelectedIndex !== pendingTextIndex) ||
                    event.key === 'Home' ?
                    -1 : 1,
                step = event.key === 'Home' || event.key === 'End' ? this.state.value.length : 1,
                nextSelectedIndex = prevSelectedIndex !== -1 ?
                    Math.max(0, Math.min(prevSelectedIndex + direction * step, pendingTextIndex)) :
                    -1;

            if (nextSelectedIndex !== prevSelectedIndex || event.key === 'Backspace' && prevSelectedIndex === pendingTextIndex) {
                this.refs[nextSelectedIndex === pendingTextIndex ? 'pending-text' : 'email-' + nextSelectedIndex].focus();

                if (event.key === 'Delete' || event.key === 'Backspace') {
                    this._onDeleteEmailClick(Math.min(prevSelectedIndex, pendingTextIndex - 1), event);
                }

                event.stopPropagation();
                event.preventDefault();
            }
        }
    }

    _onDeleteEmailClick = (index, event) => {
        if (this.props.disabled) {
            return;
        }

        this._onChange(this.state.value.filter((_, i) => i !== index), this.state.pendingText);

        this.refs['pending-text'].focus();

        event.stopPropagation();
    }

    _onBlur = (event) => {
        if (this.props.disabled) {
            return;
        }

        if (!isAncestorOrSelf(this.refs['root'], event.relatedTarget)) {
            this._onPendingTextChange(true);
        }

        event.stopPropagation();
        event.preventDefault();
    }

    _onClearClick = (event) => {
        if (this.props.disabled) {
            return;
        }

        this._onChange([], '');

        this.refs['pending-text'].focus();

        event.stopPropagation();
    }

    render() {
        const
            { placeholder, clearable, disabled } = this.props,
            { value, pendingText } = this.state,
            empty = !value.length && !pendingText;

        return (
            <div ref="root" className={
                'multi-email-input ' + this.props.className +
                (empty ? ' empty' : '') +
                (disabled ? ' disabled' : '')
            }>
                {empty ? <div className="placeholder" onClick={!disabled ? this._onClick : null}><FormattedResource resource={placeholder} /></div> : null}
                <div className="input" onKeyDown={this._onKeyDown}>
                    {value.map((email, index) => (
                        <div key={email} ref={'email-' + index} className="email" tabIndex={-1}>
                            {email}
                            {!disabled ? <button className="inline fa fa-times" tabIndex={-1} onClick={this._onDeleteEmailClick.bind(this, index)} /> : null}
                        </div>
                    ))}
                    <div ref="pending-text" className="pending-text" contentEditable suppressContentEditableWarning
                        onClick={this._onClick} onInput={this._onInput} onPaste={this._onPaste} onKeyUp={IS_IE ? this._onInput : null} onBlur={this._onBlur}>
                        {!IS_IE ? <br /> : null /*magic, check _getInnerText*/}
                    </div>
                </div>
                {
                    !empty && clearable && !disabled ?
                        <button className="fa fa-times" tabIndex="-1" onClick={this._onClearClick} /> :
                        null
                }
            </div>
        );
    }
}